import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input, Signal } from '@angular/core';

type TDueDateData = { label: string; color: string; backgroundColor?: string };

const DUE_DATE_FORMAT = {
  finished: $localize`:@@TASKS_DUE_FINISHED:Finished`,
  overdue: $localize`:@@OVERDUE:Overdue`,
  dueToday: $localize`:@@DUE_TODAY:Due today`,
  dueInDay: $localize`:@@DUE_DAY:Due in 1 day`,
  dueInDays: (time: number) => $localize`:@@DUE_DAYS:Due in ${time} days`,
  dueInWeek: $localize`:@@DUE_WEEK:Due in 1 week`,
  dueInWeeks: (time: number) => $localize`:@@DUE_WEEKS:Due in ${time} weeks`,
  dueInMonth: $localize`:@@DUE_MONTH:Due in 1 month`,
  dueInMonths: (time: number) => $localize`:@@DUE_MONTHS:Due in ${time} moths`,
  dueInYear: $localize`:@@DUE_YEAR:Due in 1 year`,
  dueInYears: (time: number) => $localize`:@@DUE_YEARS:Due in ${time} years`
};

const DAY_IN_MS = 1000 * 60 * 60 * 24;
const WEEK_IN_DAYS = 7;
const YEARS_IN_MONTHS = 12;

@Component({
  selector: 'ticket-due-date',
  standalone: true,
  template: `@if(dueDateData(); as data){
    <div
      class="due-date-container"
      [style.border-color]="data.color"
      [style.background-color]="data.backgroundColor || 'transparent'"
    >
      <span class="body_small_regular" [style.color]="data.color">{{ data.label }}</span>
    </div>
    }`,
  styleUrl: './due-date.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DueDateComponent {
  readonly color = input.required<string>();
  /** Optional when `finished` is set. */
  readonly date = input<string | Date | null | undefined>(undefined);
  /** Closed tickets: shows "Finished" in `color` instead of the relative due date. */
  readonly finished = input(false, { transform: booleanAttribute });

  readonly dueDateData: Signal<TDueDateData | null> = computed(() => {
    if (this.finished()) return { label: DUE_DATE_FORMAT.finished, color: this.color() };
    const date = this.date();
    if (!date) return null;
    return this._dueData(new Date(), new Date(date));
  });

  private _dueData(startDate: Date, endDate: Date): TDueDateData {
    const color = this.color();
    const { days, weeks, months, years } = this._calculateDateDifference(startDate, endDate);

    switch (true) {
      case years > 1:
        return { label: DUE_DATE_FORMAT.dueInYears(years), color };
      case years === 1 && months >= 12:
        return { label: DUE_DATE_FORMAT.dueInYear, color };
      case months > 1:
        return { label: DUE_DATE_FORMAT.dueInMonths(months), color };
      case months === 1 && weeks >= 4:
        return { label: DUE_DATE_FORMAT.dueInMonth, color };
      case weeks > 1:
        return { label: DUE_DATE_FORMAT.dueInWeeks(weeks), color };
      case weeks === 1 && days >= 7:
        return { label: DUE_DATE_FORMAT.dueInWeek, color };
      case days > 1:
        return { label: DUE_DATE_FORMAT.dueInDays(days), color };
      case days === 1:
        return { label: DUE_DATE_FORMAT.dueInDay, color };
      case days === 0:
        return { label: DUE_DATE_FORMAT.dueToday, color };
      default:
        return { label: DUE_DATE_FORMAT.overdue, color: '#FFF', backgroundColor: '#CD3F3E' };
    }
  }

  private _calculateYearsDifference(startDate: Date, endDate: Date): number {
    let years = endDate.getFullYear() - startDate.getFullYear();
    if (
      endDate.getMonth() < startDate.getMonth() ||
      (endDate.getMonth() === startDate.getMonth() && endDate.getDate() < startDate.getDate())
    ) {
      years--;
    }
    return years;
  }

  private _calculateMonthsDifference(startDate: Date, endDate: Date): number {
    const years = this._calculateYearsDifference(startDate, endDate);
    return years * YEARS_IN_MONTHS + (endDate.getMonth() - startDate.getMonth());
  }

  private _calculateDaysDifference(startDate: Date, endDate: Date): number {
    return Math.floor((endDate.getTime() - startDate.getTime()) / DAY_IN_MS);
  }

  private _calculateWeeksDifference(startDate: Date, endDate: Date): number {
    return Math.floor(this._calculateDaysDifference(startDate, endDate) / WEEK_IN_DAYS);
  }

  private _calculateDateDifference(startDate: Date, endDate: Date) {
    return {
      days: this._calculateDaysDifference(startDate, endDate),
      weeks: this._calculateWeeksDifference(startDate, endDate),
      months: this._calculateMonthsDifference(startDate, endDate),
      years: this._calculateYearsDifference(startDate, endDate)
    };
  }
}
